/**
 * The AI calls page (`/ai-calls`): every call the AI placed and what came of it.
 *
 * A HAND-WRITTEN MIRROR of `apps/api/src/modules/crm-calls/lib/outbound/result/`
 * (`disposition.ts` and `results.ts`); `aiOutboundResults.test.ts` reads those
 * files and holds the word lists below against them, so a disposition added on
 * the server fails a test here instead of rendering as a raw key.
 *
 * React-free: the tones, the labels' keys, the query string and the page merge
 * are the parts worth testing.
 */
import { api } from "./api";
import type { StatusTone } from "../crm/ui";

/** Mirrors `OUTBOUND_DISPOSITIONS`. */
export const OUTBOUND_DISPOSITIONS = [
  "meeting",
  "interested",
  "callback",
  "send_info",
  "send_report",
  "not_interested",
  "do_not_call",
  "wrong_person",
  "gatekeeper",
  "voicemail",
  "no_answer",
  "busy",
  "failed",
  "no_conversation",
  "record_objected",
  "unclassified",
  "taken_over",
] as const;
export type OutboundDisposition = (typeof OUTBOUND_DISPOSITIONS)[number];

/** Mirrors `RESULT_MODES`. */
export const RESULT_MODES = ["all", "test", "real"] as const;
export type ResultMode = (typeof RESULT_MODES)[number];

export interface ResultDetails {
  summary: string;
  nextStep: string;
  callbackAt: string | null;
  contactName: string;
  email: string;
  optOut: "none" | "calls" | "all";
  calleeLanguage: string | null;
}

/** The defect an alert call was placed on (ring R9). Mirrors `ResultAlert`. */
export interface ResultAlert {
  findingId: string;
  class: string;
  /** ISO: when a measurement first saw the defect. */
  firstSeenAt: string;
  /** ISO: the recheck right before the call. */
  verifiedAt: string;
  /** The opening's sentence about the defect, as spoken. */
  spokenText: string;
}

/** One row. Mirrors `AiOutboundResultRow`. */
export interface AiOutboundResultRow {
  id: string;
  startedAt: string;
  endedAt: string | null;
  durationSec: number | null;
  status: string;
  companyName: string | null;
  regCode: string | null;
  domain: string | null;
  toNumber: string;
  intendedNumber: string | null;
  test: boolean;
  kind: string | null;
  goal: string | null;
  disposition: string | null;
  resultAt: string | null;
  source: "rule" | "model" | "fallback" | null;
  fallbackReason: string | null;
  answeredBy: string | null;
  recordObjected: boolean;
  hasRecording: boolean;
  details: ResultDetails | null;
  withheld: boolean;
  /** The alert the call was about; null for an offer call, absent from an older server. */
  alert?: ResultAlert | null;
}

export interface AiOutboundResults {
  rows: AiOutboundResultRow[];
  counts: Record<string, number>;
  pending: number;
  nextBefore: string | null;
}

export interface ResultsFilter {
  mode: ResultMode;
  /** A disposition, "pending", or "" for every call. */
  disposition: string;
}

/** The query string for one page. Empty filters are left out. */
export function resultsQueryString(filter: ResultsFilter, before?: string | null, limit = 50): string {
  const params = new URLSearchParams();
  if (filter.mode !== "all") params.set("mode", filter.mode);
  if (filter.disposition) params.set("disposition", filter.disposition);
  if (before) params.set("before", before);
  params.set("limit", String(limit));
  return params.toString();
}

export function getAiOutboundResults(filter: ResultsFilter, before?: string | null): Promise<AiOutboundResults> {
  return api.get(`/admin/crm/calls/ai-outbound/results?${resultsQueryString(filter, before)}`);
}

/** A disposition as a catalogue suffix: the word, "pending" while there is none, or "unknown". */
export function dispositionKey(disposition: string | null): string {
  if (disposition === null) return "pending";
  return (OUTBOUND_DISPOSITIONS as readonly string[]).includes(disposition) ? disposition : "unknown";
}

/** How loudly a result is drawn: a win is green, a refusal red, a missed call quiet. */
export function dispositionTone(disposition: string | null): StatusTone {
  switch (disposition) {
    case "meeting":
    case "interested":
    case "callback":
    case "send_info":
    case "send_report":
    // ⚠ NOT EXHAUSTIVE: a word missing here gets the neutral tone in silence,
    // on the results page AND on the dock's card. A colleague took the call, so
    // it reads as a good outcome.
    case "taken_over":
      return "ok";
    case "not_interested":
    case "do_not_call":
    case "record_objected":
      return "lt";
    case "wrong_person":
    case "gatekeeper":
    case "unclassified":
      return "wt";
    default:
      return "mut";
  }
}

/** A later page appended, a row already shown not repeated. */
export function mergeResultPages(shown: readonly AiOutboundResultRow[], next: readonly AiOutboundResultRow[]): AiOutboundResultRow[] {
  const seen = new Set(shown.map((row) => row.id));
  return [...shown, ...next.filter((row) => !seen.has(row.id))];
}

/** Every call counted: the written results and the ones still waiting. */
export function totalResults(counts: Record<string, number>, pending: number): number {
  return Object.values(counts).reduce((sum, n) => sum + (Number.isFinite(n) ? n : 0), 0) + pending;
}

/** Whether the result took the number off the list of numbers the AI may ring. */
export function resultOptsOut(row: Pick<AiOutboundResultRow, "disposition" | "details">): boolean {
  return row.disposition === "do_not_call" || (row.details !== null && row.details.optOut !== "none");
}
